// Typed client for the /contacts/* REST API (LT-133). Shape matches
// the existing `authAPI` / `workspaceAPI` convention in api.ts.

import axios from 'axios';
import {
  Activity,
  BulkUploadResponse,
  Contact,
  ContactCreate,
  ContactLifecycleHistoryEntry,
  ContactListFilters,
  ContactUpdate,
  EmailEvent,
  LifecycleStage,
  NoteCreate,
  NoteUpdate,
  ScheduledCallback,
  ScheduledCallbackCreatePayload,
} from '../types/contact';

export interface BulkLifecycleUpdateResponse {
  updated: number;
  lifecycle_stage: LifecycleStage;
}

// LT-393 — Contact cockpit §9.6 prior-call composer endpoints.

export interface PriorCallPreviewResponse {
  composed_prompt: string;
  layers: Record<'L1' | 'L2' | 'L3' | 'L4' | 'L5', string>;
  prior_call_count: number;
  /** "operator_override" | "call_analyzer" | "none". */
  summary_source: string;
  has_override: boolean;
  overridden_at?: string | null;
}

export interface PriorCallOverridePayload {
  /** null clears the override; non-blank string sets it. */
  summary: string | null;
  reason?: string;
}

// Reuse the shared axios instance so auth + error-interceptor behaviour
// match every other call in the app. Importing api from api.ts creates a
// circular path for this early module — so we re-build a minimal instance
// here that shares the same auth interceptor via localStorage.
const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

function buildParams(filters?: ContactListFilters): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (!filters) return out;
  if (filters.lifecycle_stage) out.lifecycle_stage = filters.lifecycle_stage;
  if (filters.owner_id) out.owner_id = filters.owner_id;
  if (filters.account_id) out.account_id = filters.account_id;
  if (filters.since) out.since = filters.since;
  if (filters.until) out.until = filters.until;
  if (filters.q) out.q = filters.q;
  if (typeof filters.limit === 'number') out.limit = filters.limit;
  if (typeof filters.offset === 'number') out.offset = filters.offset;
  return out;
}

export const contactsAPI = {
  list: async (filters?: ContactListFilters): Promise<Contact[]> => {
    const r = await client.get<Contact[]>('/contacts/', { params: buildParams(filters) });
    return r.data;
  },

  get: async (id: string): Promise<Contact> => {
    const r = await client.get<Contact>(`/contacts/${id}`);
    return r.data;
  },

  create: async (payload: ContactCreate): Promise<Contact> => {
    const r = await client.post<Contact>('/contacts/', payload);
    return r.data;
  },

  update: async (id: string, patch: ContactUpdate): Promise<Contact> => {
    const r = await client.patch<Contact>(`/contacts/${id}`, patch);
    return r.data;
  },

  softDelete: async (id: string): Promise<void> => {
    await client.delete(`/contacts/${id}`);
  },

  // LT-328: manual-override bulk-edit of lifecycle_stage. Backend
  // bypasses the allowed-transitions table on this path (operator
  // chose explicitly). Cross-workspace ids are silently ignored.
  bulkEditLifecycle: async (
    contactIds: string[],
    lifecycleStage: LifecycleStage,
  ): Promise<BulkLifecycleUpdateResponse> => {
    const r = await client.post<BulkLifecycleUpdateResponse>(
      '/contacts/bulk-edit-lifecycle',
      { contact_ids: contactIds, lifecycle_stage: lifecycleStage },
    );
    return r.data;
  },

  // LT-657 — optional grouping. Pass `newListName` to create a static list
  // from the batch, or `listId` to append to an existing static list.
  // `newListName` wins server-side if both are supplied.
  bulkUpload: async (
    file: File,
    opts?: { listId?: string; newListName?: string },
  ): Promise<BulkUploadResponse> => {
    const form = new FormData();
    form.append('file', file);
    const newName = opts?.newListName?.trim();
    if (newName) {
      form.append('new_list_name', newName);
    } else if (opts?.listId) {
      form.append('list_id', opts.listId);
    }
    const r = await client.post<BulkUploadResponse>('/contacts/bulk-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },

  listActivities: async (
    contactId: string,
    { limit = 50, offset = 0 }: { limit?: number; offset?: number } = {}
  ): Promise<Activity[]> => {
    const r = await client.get<Activity[]>(`/contacts/${contactId}/activities`, {
      params: { limit, offset },
    });
    return r.data;
  },

  createNote: async (contactId: string, payload: NoteCreate): Promise<Activity> => {
    const r = await client.post<Activity>(`/contacts/${contactId}/activities`, payload);
    return r.data;
  },

  updateNote: async (
    contactId: string,
    activityId: string,
    patch: NoteUpdate
  ): Promise<Activity> => {
    const r = await client.patch<Activity>(
      `/contacts/${contactId}/activities/${activityId}`,
      patch
    );
    return r.data;
  },

  deleteNote: async (contactId: string, activityId: string): Promise<void> => {
    await client.delete(`/contacts/${contactId}/activities/${activityId}`);
  },

  // LT-167 — engagement event feed for an email Activity row.
  getEmailEvents: async (emailLogId: string): Promise<EmailEvent[]> => {
    const r = await client.get<EmailEvent[]>(`/email-logs/${emailLogId}/events`);
    return r.data;
  },

  // LT-330 — freestanding callback subsystem.
  listScheduledCallbacks: async (
    contactId: string,
    { includeTerminal = true }: { includeTerminal?: boolean } = {},
  ): Promise<ScheduledCallback[]> => {
    const r = await client.get<ScheduledCallback[]>(
      `/contacts/${contactId}/scheduled-callbacks`,
      { params: { include_terminal: includeTerminal } },
    );
    return r.data;
  },

  createScheduledCallback: async (
    contactId: string,
    payload: ScheduledCallbackCreatePayload,
  ): Promise<ScheduledCallback> => {
    const r = await client.post<ScheduledCallback>(
      `/contacts/${contactId}/scheduled-callbacks`,
      payload,
    );
    return r.data;
  },

  rescheduleScheduledCallback: async (
    contactId: string,
    commitmentId: string,
    newScheduledAt: string,
  ): Promise<ScheduledCallback> => {
    const r = await client.post<ScheduledCallback>(
      `/contacts/${contactId}/scheduled-callbacks/${commitmentId}/reschedule`,
      { new_scheduled_at: newScheduledAt },
    );
    return r.data;
  },

  cancelScheduledCallback: async (
    contactId: string,
    commitmentId: string,
  ): Promise<ScheduledCallback> => {
    const r = await client.delete<ScheduledCallback>(
      `/contacts/${contactId}/scheduled-callbacks/${commitmentId}`,
    );
    return r.data;
  },

  // LT-360 — Lifecycle audit trail.
  getLifecycleHistory: async (
    contactId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<ContactLifecycleHistoryEntry[]> => {
    const r = await client.get<ContactLifecycleHistoryEntry[]>(
      `/contacts/${contactId}/lifecycle-history`,
      { params },
    );
    return r.data;
  },

  // LT-393 — Contact cockpit §9.6 prior-call composer.

  getPriorCallPreview: async (
    contactId: string,
  ): Promise<PriorCallPreviewResponse> => {
    const r = await client.get<PriorCallPreviewResponse>(
      `/contacts/${contactId}/prior-call-preview`,
    );
    return r.data;
  },

  setPriorCallOverride: async (
    contactId: string,
    payload: PriorCallOverridePayload,
  ): Promise<Contact> => {
    const r = await client.post<Contact>(
      `/contacts/${contactId}/prior-call-override`,
      payload,
    );
    return r.data;
  },
};
